// ============================================================
//  src/admin/context/AdminAuthContext.js
// ============================================================
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { postAdminLogin, postAdminLogout, getSiteSettings } from '../api/adminClient';

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [admin,   setAdmin]   = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Bootstrap from localStorage ───────────────────────
  useEffect(() => {
    const stored = localStorage.getItem('adminUser');
    const token  = localStorage.getItem('adminToken');
    if (stored && token) {
      try { setAdmin(JSON.parse(stored)); }
      catch { localStorage.removeItem('adminUser'); }
    }
    setLoading(false);
  }, []);

  // ── Login ──────────────────────────────────────────────
  const login = useCallback(async (username, password, pin = '') => {
    try {
      const { data: res } = await postAdminLogin({ username, password, ...(pin ? { pin } : {}) });

      if (res.status === 'pinrequired') {
        return res;
      }

      if (res.status === 'success') {
        const adminUser = {
          name:     res.name,
          username: res.username,
          role:     res.role,
          token:    res.token,
        };
        setAdmin(adminUser);
        localStorage.setItem('adminUser',  JSON.stringify(adminUser));
        localStorage.setItem('adminToken', res.token);

        // Fetch and store site name for use across the app
        try {
          const { data: s } = await getSiteSettings();
          if (s?.data?.sitename) {
            localStorage.setItem('adminSiteName', s.data.sitename);
          }
        } catch { /* non-critical, silently ignore */ }

        return res;
      }

      throw new Error(res.msg || 'Login failed');

    } catch (err) {
      const msg = err?.response?.data?.msg || err?.message || 'Login failed';
      throw new Error(msg);
    }
  }, []);

  // ── Logout ─────────────────────────────────────────────
  const logout = useCallback(async () => {
    try { await postAdminLogout(); } catch { /* silent */ }
    setAdmin(null);
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminSiteName');
  }, []);

  return (
    <AdminAuthContext.Provider value={{ admin, loading, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export const useAdminAuth = () => useContext(AdminAuthContext);